import type { Api_Playlist, Api_Video } from '@core/api'
import { PlayerLayout } from '@core/player/types'
import type { TriggerSourceEnum } from '@core/tracking/triggerSourceTracking'
import type { GetState, NamedSet } from '@core/zustand'

import type { EmbedFeedState } from './store'

export type EmbedFeedActions = ReturnType<typeof createEmbedFeedActions>

/**
 * Creates object with actions for EmbedFeed store
 */
export const createEmbedFeedActions = (
  set: NamedSet<EmbedFeedState>,
  get: GetState<EmbedFeedState>,
) => ({
  resetPlaceholders: () => {
    set({ placeholders: [] })
  },

  setThumbnailLoaded: (video: Api_Video) => {
    const { thumbnailIdsLoaded } = get()
    if (!thumbnailIdsLoaded[video.encoded_id]) {
      set(
        (state) => ({
          thumbnailIdsLoaded: {
            ...state.thumbnailIdsLoaded,
            [video.encoded_id]: true,
          },
        }),
        false,
        'setThumbnailLoaded',
      )
    }
  },

  registerThumbnailImpression: (video: Api_Video) => {
    const {
      thumbnailIdsLoaded: loaded,
      thumbnailImpressionIdsReportSuccess: success,
    } = get()

    if (loaded[video.encoded_id] && !success[video.encoded_id]) {
      set(
        (state) => ({
          thumbnailImpressionVideoReportQueue: [
            ...state.thumbnailImpressionVideoReportQueue,
            video,
          ],
        }),
        false,
        'registerThumbnailImpression',
      )
    }
  },

  processThumbnailImpressionQueue: (videos: Api_Video[]) => {
    if (videos.length) {
      const newSuccess = videos.reduce<Record<string, boolean>>(
        (acc, video) => {
          return {
            ...acc,
            [video.encoded_id]: true,
          }
        },
        {},
      )

      set(
        (state) => ({
          thumbnailImpressionVideoReportQueue: [],
          thumbnailImpressionIdsReportSuccess: {
            ...state.thumbnailImpressionIdsReportSuccess,
            ...newSuccess,
          },
        }),
        false,
        'processThumbnailImpressionQueue',
      )
    }
  },

  showVideoPlayer: ({
    video,
    playlist,
  }: {
    video?: Api_Video | null
    playlist?: Api_Playlist
  }) => {
    set(
      () => ({
        player: {
          video,
          playlist,
          isOpen: true,
          layout: PlayerLayout.FULLSCREEN,
        },
      }),
      false,
      'showVideoPlayer',
    )
  },

  setVideoPlayerLayout: (layout: PlayerLayout) => {
    set(
      (state) => ({
        player: { ...state.player, layout },
      }),
      false,
      'setVideoPlayerLayout',
    )
  },

  hideVideoPlayer: () => {
    set(
      (state) => ({
        player: {
          ...state.player,
          isOpen: false,
          layout: PlayerLayout.DEFAULT,
        },
      }),
      false,
      'hideVideoPlayer',
    )
  },

  registerInViewport: (video: Api_Video) => {
    const { inViewportIds, inViewportIdsReportSuccess } = get()

    if (!inViewportIds[video.encoded_id]) {
      const alreadyTracked = inViewportIdsReportSuccess.includes(
        video.encoded_id,
      )

      if (alreadyTracked) {
        set(
          (state) => ({
            inViewportIds: {
              ...state.inViewportIds,
              [video.encoded_id]: true,
            },
          }),
          false,
          'registerInViewport',
        )
        return
      }

      set(
        (state) => ({
          inViewportIdsReportQueue: [
            ...state.inViewportIdsReportQueue,
            video.encoded_id,
          ],
          inViewportIds: {
            ...state.inViewportIds,
            [video.encoded_id]: true,
          },
          feedVideosInViewportQueue: [
            ...state.feedVideosInViewportQueue,
            video,
          ],
        }),
        false,
        'registerInViewport',
      )
    }
  },

  unregisterInViewport: (video: Api_Video) => {
    const { inViewportIds } = get()

    if (inViewportIds[video.encoded_id]) {
      set(
        (state) => ({
          inViewportIds: {
            ...state.inViewportIds,
            [video.encoded_id]: false,
          },
        }),
        false,
        'unregisterInViewport',
      )
    }
  },

  /**
   * Registers video ids as successfully processed and resets the queue.
   *
   * @param newInViewportIds - Ids that are considered new obtained from the queue
   */
  processInViewportQueue: (newInViewportIds: string[], videos: Api_Video[]) => {
    if (newInViewportIds.length) {
      set(
        (state) => ({
          inViewportIdsReportQueue: [],
          inViewportIdsReportSuccess: [
            ...state.inViewportIdsReportSuccess,
            ...newInViewportIds,
          ],
          feedVideosInViewportSuccess: [
            ...state.feedVideosInViewportSuccess,
            ...videos,
          ],
          feedVideosInViewportQueue: [],
        }),
        false,
        'processInViewportQueue',
      )
    }
  },

  /**
   * Explicitly set video thumbnail to autoplay. Used
   * when user hovers over a video thumbnail.
   */
  setAutoplay: (videoId: string) => {
    const { inViewportIds, autoplayId } = get()

    if (inViewportIds[videoId] && autoplayId !== videoId) {
      set(
        {
          autoplayId: videoId,
          pausedAutoplayId: null,
          hasAutoplayedOnce: true,
        },
        false,
        'setAutoplay',
      )
    }
  },

  /**
   * Queues a user-gesture cause (e.g. arrow click) to be applied to the next
   * viewport-resolved autoplay. Pass `null` to clear it once consumed.
   */
  setPendingTriggerSource: (source: TriggerSourceEnum | null) => {
    set({ pendingTriggerSource: source }, false, 'setPendingTriggerSource')
  },

  /**
   * Resets autoplay so none of the videos is playing.
   */
  pauseAutoplay: () => {
    set(
      {
        autoplayId: null,
        pausedAutoplayId: get().autoplayId ?? get().pausedAutoplayId,
      },
      false,
      'pauseAutoplay',
    )
  },
})
